/**
 * App shell — the persistent SIMULATION notice, the dark/light switcher, and eight tabs.
 *
 * ── THE TAB SET, AND WHY IT IS THIS SHORT ───────────────────────────────────────────────────
 *
 * FOUR vendable use cases, the findings summary, the architecture, the load-test results, per-pool
 * capacity, and the endpoint configuration. Eight tabs became six by MERGING rather than deleting;
 * the findings summary and UC7 are the additions since:
 *
 *   * UC7 IS NOW DEPLOYED AND VENDS, AND IT IS STILL NOT MEASURED — three states, not two. It was
 *     the one option tab that was not built, its label said "PLANNED, not built", it took no props
 *     because it called nothing, and it had no vend control. All of that was correct then and all of
 *     it is now wrong: `/v7` returns real barcodes from an Aurora PostgreSQL Global Database in both
 *     regions. So the label, the subtitle below and the panel were flipped to DEPLOYED — and
 *     deliberately NOT to "measured". No load test, no switchover, no failover drill and no patch
 *     test has been run, so the tab still carries NO performance figure of any kind. Mistaking a
 *     deployment for a measurement is now the more dangerous of the two errors, because it attaches
 *     numbers to something nobody benchmarked. See Uc7Panel.jsx.
 *
 *   * THREE benchmark tabs (in-region / multi-region / reclamation ON-vs-OFF) became ONE
 *     "Load test" tab. They were separate because their figures are genuinely not
 *     interchangeable — and that reason still holds, so the merged tab does not merge the
 *     numbers: it shows the metrics that decide something (p50/p99 per region and per use case,
 *     duplicate PICs, gap ratio, the two 429 kinds kept apart) and leaves the ladders,
 *     driver-headroom CPU tables and per-cell provenance in loadtest/results/*.md, which is the
 *     artifact of record. Publishing everything on one page and publishing nothing legible are
 *     closer together than they look.
 *
 *   * The "Findings & recommendation" tab is a SUMMARY of docs/FINDINGS.md (~1,600 lines), for the
 *     same reason: the recommendation, the per-use-case status, the live caveats and §6.2 are on
 *     screen, and the test tables, per-option comparisons and capacity arithmetic stay in the
 *     document. Generated at build time — see web/findings-summary.mjs and FindingsPanel.jsx.
 *
 * ── THE INTERFACE DOES NOT NAME THE CUSTOMER, AND THAT IS NOT A BLANKET RULE ─────────────────
 *
 * The heading, the document title and the footer are neutral, and the three localStorage keys plus
 * the pre-paint data attribute were renamed off the customer's name. Three classes of occurrence
 * were deliberately KEPT, and src/lib/caveats.js carries the full reasoning so a future
 * find-and-replace finds it before running: the barcode SPECIFICATION's document number (a citation
 * — renaming it would misattribute a public standard), a real PUBLIC SERVICE NAME in the rules
 * catalogue, and the DO-NOT-MAIL WARNING, which has to name the mail network that will not
 * recognise these barcodes or it reads as generic boilerplate.
 *
 * The keys were MIGRATED, not reset — src/boot.js explains why that mattered more than tidiness:
 * it reads the colour mode before paint, so a rename it could not follow would have flashed the
 * wrong colour at every returning visitor.
 *
 * ── THE EDITORIAL RULE APPLIED THROUGHOUT ───────────────────────────────────────────────────
 *
 * These panels show CURRENT STATE, not the history of how it was reached. Retractions,
 * "corrected on <date>" notes and inline defect numbers moved to docs/DECISION-HISTORY.md and
 * docs/FINDINGS-ADVERSARIAL-REVIEW.md. ⚠️ A LIVE CAVEAT IS CURRENT STATE and stays — the
 * simulation warning, the excluded Apigee hop, every rps being a lower bound, reclamation
 * shipping off and never having run, UC4's zero-outage claim being read rather than measured, and
 * the unreachable C05/C06. Those live in src/lib/caveats.js so there is exactly one copy of each.
 *
 * ⚠️ AND A CAVEAT THAT HAS BECOME FALSE IS NOT A CAVEAT — IT IS A DEFECT. The UC7 strings said
 * "UC7 IS A PLAN, NOT A DEPLOYMENT" and "NOTHING ABOUT UC7 HAS BEEN MEASURED". Both were true when
 * written and both went false when UC7 was built, and a stale caveat is the worst kind of stale text
 * on this site: it renders perfectly, every presence assertion keeps passing, and it discredits the
 * strict caveats beside it. So they were NARROWED to what remains true — deployed, and not measured —
 * rather than deleted or left up. Same treatment the region-drill caveat already received.
 *
 * WHAT "LIVE" MEANS HERE, AND WHY IT IS STRICTER THAN "REACHABLE": a placeholder Lambda can sit
 * behind a custom domain answering HTTP 200 with `placeholder: true` and empty payloads. A naive
 * `res.ok` health check calls that LIVE, and the UI would then present an empty serial array as a
 * successful vend. So a placeholder response is explicitly NOT live — never claim what was not
 * measured.
 *
 * The UI is Cloudscape with dark mode as the default and a working light switcher, which is why
 * there is a build step at all.
 */

import { useCallback, useEffect, useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Tabs from '@cloudscape-design/components/tabs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Toggle from '@cloudscape-design/components/toggle';
import Flashbar from '@cloudscape-design/components/flashbar';
import Uc1Panel from './components/Uc1Panel.jsx';
import Uc2Panel from './components/Uc2Panel.jsx';
import Uc4Panel from './components/Uc4Panel.jsx';
import Uc7Panel from './components/Uc7Panel.jsx';
import ArchitecturePanel from './components/ArchitecturePanel.jsx';
import FindingsPanel from './components/FindingsPanel.jsx';
import LoadTestPanel from './components/LoadTestPanel.jsx';
import CapacityPanel from './components/CapacityPanel.jsx';
import ConfigPanel from './components/ConfigPanel.jsx';
import { Barcode } from './lib/impb.js';
import { SIMULATION, SITE_VERDICT } from './lib/caveats.js';
import {
  resolveEndpoints,
  timedFetch,
  saveEndpoints,
  clearSavedEndpoints,
  isPlaceholder,
  CONTRACT_DEFAULTS,
} from './lib/api.js';
import { initialMode, applyColorMode } from './lib/theme.js';

const FORCE_LOCAL_KEY = 'demo-force-local';

/**
 * The key this one replaced. Read as a fallback so a returning user's forced-local setting survives.
 *
 * ⚠️ MIGRATED RATHER THAN RESET, for consistency with the other two keys — see src/boot.js for the
 * reasoning that actually matters (the pre-paint white flash) and src/lib/api.js for the saved
 * endpoints. This one is the least consequential of the three: losing it means the page probes the
 * real backend when the user had asked it not to, which is visible and one click to restore. It is
 * migrated anyway, because three keys with two different behaviours is a thing someone has to
 * remember, and the migration is four lines.
 */
const FORCE_LOCAL_KEY_LEGACY = 'usps-demo-force-local';

/** Forced-local flag, reading the legacy key once and copying it forward. */
function initialForceLocal() {
  try {
    const stored = localStorage.getItem(FORCE_LOCAL_KEY);
    if (stored === '1' || stored === '0') return stored === '1';
    const legacy = localStorage.getItem(FORCE_LOCAL_KEY_LEGACY);
    if (legacy === '1' || legacy === '0') {
      try {
        localStorage.setItem(FORCE_LOCAL_KEY, legacy);
        localStorage.removeItem(FORCE_LOCAL_KEY_LEGACY);
      } catch (_e2) {
        /* the migration will not persist; the value still applies for this session */
      }
      return legacy === '1';
    }
    return false;
  } catch (_e) {
    return false;
  }
}

export default function App() {
  const [mode, setMode] = useState(initialMode);
  const [endpoints, setEndpoints] = useState({ ...CONTRACT_DEFAULTS, source: 'resolving…' });
  const [probes, setProbes] = useState({ serial: null, rules: null });
  const [probing, setProbing] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [uc2Metrics, setUc2Metrics] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState([]);
  const [activeTab, setActiveTab] = useState('uc1');
  const [forceLocal, setForceLocal] = useState(initialForceLocal);

  // Reassert the mode through Cloudscape's own applyMode on mount, so the class the pre-paint
  // script set and Cloudscape's internal state cannot disagree.
  useEffect(() => {
    applyColorMode(mode);
  }, [mode]);

  // Fail loudly if the hand-transcribed Code 128 table is corrupt — a bad table must never render
  // a barcode that looks plausible.
  useEffect(() => {
    try {
      Barcode.verifyPatternTable();
    } catch (err) {
      setFlash([
        {
          type: 'error',
          header: 'Code 128 pattern table failed verification',
          content: `${err.message} — barcode rendering is not trustworthy in this build.`,
          dismissible: false,
          id: 'pattern-table',
        },
      ]);
    }
  }, []);

  const probe = useCallback(async (eps) => {
    setProbing(true);
    const [serial, rules] = await Promise.all([
      timedFetch(`${eps.serialApi}/v1/health`, {}, 6000),
      timedFetch(`${eps.rulesApi}/v2/health`, {}, 6000),
    ]);
    const decorate = (r) => ({ ...r, placeholder: isPlaceholder(r.json) });
    const next = { serial: decorate(serial), rules: decorate(rules) };
    setProbes(next);
    setProbing(false);
    return next;
  }, []);

  const refreshMetrics = useCallback(
    async (eps, usable) => {
      setRefreshing(true);
      if (!usable) {
        // INVARIANT #12 honesty: no usable backend means no allocated/issued truth. Report
        // nothing rather than zero, which would falsely imply zero waste.
        setMetrics({});
        setUc2Metrics({});
        setRefreshing(false);
        return;
      }
      const [m1, health, m2] = await Promise.all([
        timedFetch(`${eps.serialApi}/v1/metrics`, {}, 8000),
        timedFetch(`${eps.serialApi}/v1/health`, {}, 8000),
        // Amendment A7 — UC2 exposes the same allocated/issued/gapRatio shape.
        timedFetch(`${eps.rulesApi}/v2/metrics`, {}, 8000),
      ]);
      const usableJson = (r) => (r.ok && r.json && !isPlaceholder(r.json) ? r.json : null);
      const j1 = usableJson(m1);
      const jh = usableJson(health);
      setMetrics({
        ...(j1 || {}),
        ...(jh && typeof jh.bufferRemaining === 'number' ? { bufferRemaining: jh.bufferRemaining } : {}),
        ...(jh && jh.regionId ? { regionId: jh.regionId } : {}),
        // Amendment A18. Carried through so the UI can state feature state as FACT read from
        // the running service, not infer it from the repo. A server predating A18 omits
        // `features` entirely, which renders as "unknown" rather than a misleading "off".
        ...(jh && jh.features && typeof jh.features === 'object' ? { features: jh.features } : {}),
      });
      // A7 is new; a server that predates it answers 404 or echoes the labels shape. Only accept a
      // response that actually carries the counters, so a missing endpoint reads n/a not 0.
      const j2 = usableJson(m2);
      setUc2Metrics(j2 && typeof j2.serialsAllocated === 'number' ? j2 : {});
      setRefreshing(false);
    },
    []
  );

  // Boot: resolve endpoints, probe, then load metrics.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const eps = await resolveEndpoints();
      if (cancelled) return;
      setEndpoints(eps);
      const p = await probe(eps);
      if (cancelled) return;
      const usable = !forceLocal && (isUsable(p.serial) || isUsable(p.rules));
      await refreshMetrics(eps, usable);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serialLive = !forceLocal && isUsable(probes.serial);
  const rulesLive = !forceLocal && isUsable(probes.rules);

  const localReason = forceLocal
    ? 'Local render mode is forced on in the Endpoints tab.'
    : probes.serial && probes.serial.placeholder
      ? 'The deployed endpoint is the infra PLACEHOLDER (HTTP 200 with placeholder: true) — services/ is not behind that route yet, so there is nothing real to call.'
      : probes.serial && !probes.serial.ok
        ? `No backend reachable at ${endpoints.serialApi}${probes.serial.error ? ` (${probes.serial.error})` : ''}.`
        : 'No usable backend.';

  async function onToggleMode(checked) {
    const next = checked ? 'dark' : 'light';
    setMode(next);
    applyColorMode(next);
  }

  async function onSaveEndpoints(serialApi, rulesApi) {
    const eps = {
      serialApi: serialApi.trim().replace(/\/+$/, '') || CONTRACT_DEFAULTS.serialApi,
      rulesApi: rulesApi.trim().replace(/\/+$/, '') || CONTRACT_DEFAULTS.rulesApi,
      source: 'saved-in-browser (localStorage)',
    };
    saveEndpoints(eps.serialApi, eps.rulesApi);
    setEndpoints(eps);
    const p = await probe(eps);
    await refreshMetrics(eps, !forceLocal && (isUsable(p.serial) || isUsable(p.rules)));
  }

  async function onResetEndpoints() {
    clearSavedEndpoints();
    const eps = await resolveEndpoints();
    setEndpoints(eps);
    const p = await probe(eps);
    await refreshMetrics(eps, !forceLocal && (isUsable(p.serial) || isUsable(p.rules)));
    return eps;
  }

  function onForceLocal(checked) {
    setForceLocal(checked);
    try {
      localStorage.setItem(FORCE_LOCAL_KEY, checked ? '1' : '0');
    } catch (_e) {
      /* ignore */
    }
    refreshMetrics(endpoints, !checked && (isUsable(probes.serial) || isUsable(probes.rules)));
  }

  return (
    <AppLayout
      navigationHide
      toolsHide
      /*
        maxContentWidth is set EXPLICITLY because Cloudscape's default caps content at a
        readable-prose width (~1040px [1] / 1280px depending on version). On a wide monitor that
        left the app rendering in a narrow column with a large unpainted gutter to its right,
        which reads as a broken page rather than a deliberate reading measure.

        Number.MAX_SAFE_INTEGER is Cloudscape's own documented idiom for "no cap" — the prop takes
        a number of pixels, so there is no `none` sentinel. Paired with the html/body rule in
        index.html: the cap governed the CONTENT box, and the missing body background is what made
        the leftover space read as empty rather than as page.
      */
      maxContentWidth={Number.MAX_SAFE_INTEGER}
      notifications={flash.length ? <Flashbar items={flash} /> : undefined}
      content={
        <ContentLayout
          header={
            <SpaceBetween size="m">
              <Header
                variant="h1"
                /*
                  ⚠️ THE SPLIT IN THIS SENTENCE MOVED, IT DID NOT DISAPPEAR — AND THAT IS THE POINT.

                  It used to read "Three DEPLOYED architectures ... plus one PLANNED option that is
                  not built and not measured", because UC7 was a design and describing four
                  architectures without splitting them would have made the page's own subtitle the
                  first place a reader mistook a plan for a deployment.

                  All four are now deployed and all four vend. The split then became
                  MEASURED-vs-UNMEASURED — and THAT has now gone stale too, which makes this the
                  FIFTH generation of the same defect in this one sentence. UC7 is measured: the
                  load test ran, and so did a live engine patch, a managed switchover and an
                  --allow-data-loss failover. The old text promised a reader "no load test, no
                  failover drill", which is now simply false.

                  ⚠️ THE SPLIT THAT REPLACES IT IS MEASURED-vs-CHOSEN, and it is the last one this
                  sentence needs, because it turns on a DECISION rather than on work outstanding.
                  Work-outstanding claims rot every time work is done; a decision only changes when
                  the decision changes. Collapsing it to "four measured architectures" would make
                  this subtitle the first place a reader assumed all four are still candidates.
                */
                description="Four DEPLOYED and MEASURED architectures for high-burst package-serial vending — measured rather than asserted. UC4 is the recommendation; UC7 was built, measured and NOT CHOSEN, on evidence rather than on paper. Vend a real barcode on any of the first four tabs."
                actions={
                  <Toggle checked={mode === 'dark'} onChange={({ detail }) => onToggleMode(detail.checked)} data-testid="mode-toggle">
                    {mode === 'dark' ? 'Dark mode' : 'Light mode'}
                  </Toggle>
                }
              >
                Serial Vending Demo
              </Header>

              {/*
                SIMULATION NOTICE. Non-dismissible on purpose: a customer must not be able to lose
                it, and a screenshot taken from any scroll position must carry it. The words live in
                lib/caveats.js so there is one copy, and scripts/verify-web-live.sh asserts it
                renders in the live DOM.
              */}
              <Alert type="warning" header={SIMULATION.header} data-testid="sim-banner">
                <Box variant="p">
                  {SIMULATION.midsAreFabricated} {SIMULATION.neverPrint}
                </Box>
              </Alert>

              {/*
                THE VERDICT, ON EVERY TAB, BECAUSE IT WAS ONLY REACHABLE ON THE SIXTH.

                An adversarial readability audit of all nine tabs found the recommendation was
                discoverable only on the Findings tab — the 6th of 9, AFTER four option tabs. A
                reader working left to right therefore met four detailed options before learning
                that one of them was recommended and one had been rejected. The rationale itself
                was judged the best-written thing on the site; the problem was purely placement.

                ⚠️ DELIBERATELY *BELOW* THE SIMULATION BANNER. The banner is legally load-bearing
                and must stay first from any scroll position. A recommendation never outranks it.

                ⚠️ AND DELIBERATELY NOT A TAB REORDER, which was the other option considered.
                Moving Findings to first position would have been the "obvious" fix; it is a large
                blast radius for a small gain — verify-web-live.sh carries generation-counted
                assertions on tab order and on the subtitle wording, and the current order groups
                the four vendable options together, which is what a reader exploring the demo
                wants. One line above the strip fixes discoverability on all nine tabs at once.

                The words live in lib/caveats.js so there is ONE copy; see SITE_VERDICT for why
                "MEASURED" and "NOT CHOSEN" may never be separated.
              */}
              <Box variant="p" fontWeight="bold" data-testid="site-verdict">
                {SITE_VERDICT}
              </Box>
            </SpaceBetween>
          }
        >
          <Tabs
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            tabs={[
              {
                id: 'uc1',
                label: 'UC1 — Vend serials (DynamoDB)',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <Uc1Panel
                      mode={mode}
                      endpoints={endpoints}
                      live={serialLive}
                      liveProbe={{ reason: localReason }}
                      metrics={metrics}
                      refreshing={refreshing}
                      onRefreshMetrics={async () => {
                        const p = await probe(endpoints);
                        await refreshMetrics(endpoints, !forceLocal && (isUsable(p.serial) || isUsable(p.rules)));
                      }}
                    />
                  </Box>
                ),
              },
              {
                id: 'uc2',
                label: 'UC2 — Vend labels (Aurora PostgreSQL)',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <Uc2Panel
                      mode={mode}
                      endpoints={endpoints}
                      live={rulesLive}
                      liveProbe={{ reason: localReason }}
                      uc2Metrics={uc2Metrics}
                      onRefreshUc2Metrics={async () => {
                        const p = await probe(endpoints);
                        await refreshMetrics(endpoints, !forceLocal && (isUsable(p.serial) || isUsable(p.rules)));
                      }}
                    />
                  </Box>
                ),
              },
              {
                /*
                  UC4 vends through Aurora DSQL for real. Every /v4 response must report
                  engine === 'aurora-dsql' or the panel treats it as an ERROR, and the routes
                  refuse with 503 rather than falling back to Aurora PostgreSQL — so a result
                  under a DSQL heading cannot have come from a different engine. There is
                  deliberately no local-render fallback here, unlike UC1/UC2: a JavaScript
                  stand-in cannot demonstrate which engine answered.
                */
                id: 'uc4',
                label: 'UC4 — Vend labels (Aurora DSQL, active-active)',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <Uc4Panel
                      mode={mode}
                      endpoints={endpoints}
                      live={rulesLive}
                      liveProbe={{ reason: localReason }}
                    />
                  </Box>
                ),
              },
              {
                /*
                  UC7 — DEPLOYED AND VENDING, AND THE ONLY TAB HERE THAT IS NOT MEASURED.

                  Placed AFTER the other three vendable tabs and BEFORE the findings, which still
                  does real work but a different job than before. It used to be so that the
                  convention "an option tab has a vend button" was established by UC1/UC2/UC4 first,
                  making its ABSENCE here legible. UC7 now has a vend button, so the ordering instead
                  puts the three MEASURED options before the one that is not, and keeps UC7 out of
                  the position a reader scans first.

                  ⚠️ THE TAB LABEL CARRIES THE VERDICT, AND THE QUALIFIER IT CARRIES HAS NOW MOVED
                  TWICE. It said "PLANNED, not built", then "DEPLOYED, not yet measured". The reason
                  never changed — the tab strip is what a reader scans and what a screenshot of the
                  tab row captures, so a bare "UC7 — Aurora PostgreSQL Global Database" beside three
                  labels naming measured options would imply a fourth measured option. Only the FACT
                  changed, twice, and each time the label went stale while rendering perfectly.

                  "not yet measured" is now FALSE: the load test, a live minor-version engine patch,
                  a managed switchover and an --allow-data-loss failover all ran. So the guarded
                  split moved from deployed-vs-measured to MEASURED-vs-CHOSEN. That is the durable
                  form, because it turns on a DECISION rather than on work outstanding — a
                  work-outstanding claim rots every time work is done, which is precisely why this
                  string has now been wrong twice.

                  ⚠️ "MEASURED" HERE MEANS THE TOPOLOGY EVENTS WERE DRIVEN AND OBSERVED. It does NOT
                  mean UC7 met the availability bar: four rows on its own tab argue against it, and
                  §9 of the evaluation concludes against adopting it. So NOT CHOSEN travels in the
                  same string, always. Never ship a bare "(measured)", and never upgrade it to
                  "validated", "tested", "passed" or "proven" — each claims a bar was met.

                  It now takes the SAME props as Uc4Panel, because it calls something. The old
                  comment said handing it an endpoint would be "the first step toward someone wiring
                  up a control that cannot work" — the control works, and the endpoint is what the
                  panel asserts the engine and the global role against.
                */
                id: 'uc7',
                label: 'UC7 — Aurora PostgreSQL Global Database (DEPLOYED, MEASURED — NOT CHOSEN)',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <Uc7Panel
                      mode={mode}
                      endpoints={endpoints}
                      live={rulesLive}
                      liveProbe={{ reason: localReason }}
                    />
                  </Box>
                ),
              },
              {
                /*
                  FINDINGS — the recommendation document, made readable beside the working demo.
                  Placed immediately after the three vendable tabs and BEFORE the architecture,
                  because it is the ANSWER and the diagrams are its illustration.

                  It is a SUMMARY, not the document: ~1,600 lines rendered into a tab would rebuild
                  the wall of text the declutter removed. The recommendation, the per-use-case
                  status and the live caveats are on screen; §5's tables stay on the Load test tab
                  and §4's arithmetic stays in the document. Generated at build time by
                  web/findings-summary.mjs, which publishes PROSE ONLY and fails the build if a
                  measurement appears in a sentence it lifts — so no figure here can be a retyped
                  copy of one in the markdown. See FindingsPanel.jsx.
                */
                id: 'findings',
                label: 'Findings & recommendation',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <FindingsPanel metrics={metrics} live={serialLive || rulesLive} />
                  </Box>
                ),
              },
              {
                id: 'architecture',
                label: 'Architecture',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <ArchitecturePanel />
                  </Box>
                ),
              },
              {
                /*
                  ONE load-test tab, replacing three. The three were separate because their
                  figures are genuinely NOT interchangeable — a single-coast generator aimed at
                  another region measures the internet path while being labelled per-region
                  latency, and a flag-versus-itself matrix drives some cells at saturation
                  deliberately. That reason still holds, so this tab does not merge the NUMBERS:
                  it selects the metrics that decide something and labels each with the run it
                  came from. The ladders, driver-headroom CPU tables and per-cell provenance stay
                  in loadtest/results/*.md, which is the artifact of record.

                  READ-ONLY, and that is the security decision rather than a missing feature:
                  these APIs are unauthenticated by design and this page is public, so a browser
                  trigger would DoS our own endpoints, spend EC2 money and irreversibly burn
                  serials from a finite regulated field. Reasoning in LoadTestPanel.jsx.
                */
                id: 'loadtest',
                label: 'Load test',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <LoadTestPanel />
                  </Box>
                ),
              },
              {
                /*
                  Capacity is a READ (GET /v1/pools), so unlike the load test it is safe to
                  trigger from a button: it allocates nothing and burns no serials.
                */
                id: 'capacity',
                label: 'Capacity',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <CapacityPanel
                      endpoints={endpoints}
                      live={serialLive}
                      liveProbe={{ reason: localReason }}
                    />
                  </Box>
                ),
              },
              {
                id: 'config',
                label: 'Endpoints & mode',
                content: (
                  <Box margin={{ top: 'l' }}>
                    <ConfigPanel
                      endpoints={endpoints}
                      probes={probes}
                      live={serialLive || rulesLive}
                      forceLocal={forceLocal}
                      onForceLocal={onForceLocal}
                      onSave={onSaveEndpoints}
                      onReset={onResetEndpoints}
                      probing={probing}
                    />
                  </Box>
                ),
              },
            ]}
          />

          {/*
            THE DISCLAIMER KEEPS ITS FORCE WHILE DROPPING THE ATTRIBUTION.

            The customer's name is gone from the heading and the title, so naming them here would
            put it straight back. But "not a product of X, not endorsed by X" is doing real work —
            a reader must not take this for an official implementation — so the sentence is
            rewritten around the RELATIONSHIP rather than the NAME: the postal operator whose
            published specification this implements. That still says "nobody official blessed this",
            which is the whole point of the line, and it is if anything stronger than the original
            because it also states what the demo's relationship to that operator actually is.
          */}
          <Box margin={{ top: 'xl' }} variant="small" color="text-body-secondary" textAlign="center">
            Demo for a customer architecture conversation. Not a product of, and not endorsed by, the
            postal operator whose published specification it implements.{' '}
            <strong>Simulation only — not valid for mailing.</strong>
          </Box>
        </ContentLayout>
      }
    />
  );
}

/** Reachable AND not the infra placeholder. See the header note. */
function isUsable(probe) {
  return Boolean(probe && probe.ok && !probe.placeholder);
}
